// src/contexts/ServerConnectionContext.js
import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from './AuthContext';
import {
    getServerInfoAppEndpoint,
    WS_SERVER_INFO_TOPICS,
    getServerInfoUserQueueTopic,
    WS_SERVER_INFO_TYPES
} from '../config/wsServerInfoConfig';

export const ServerConnectionContext = createContext({
    serverInfo: null,
    isConnectedToServer: false,
    isConnecting: false,
    connectionError: null,
    targetIp: null, // The IP we are currently targeting/connected to
    connectToServer: (ip) => {},
    disconnectFromServer: () => {},
});

export const useServerConnection = () => useContext(ServerConnectionContext);

export const ServerConnectionProvider = ({ children }) => {
    const [serverInfo, setServerInfo] = useState(null);
    const [isConnectedToServer, setIsConnectedToServer] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [targetIp, setTargetIp] = useState(null); // Track the target IP

    const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
    const { isAuthenticated } = useAuth();
    const subscriptions = useRef({ info: null, error: null });

    // --- Disconnect Logic ---
    const disconnectFromServer = useCallback((sendMessageToServer = true) => {
        console.log(`ServerConnectionContext: Disconnecting (Send WS: ${sendMessageToServer}). Current Target: ${targetIp}`);
        if (sendMessageToServer && targetIp && isWebSocketConnected) {
            // Optional: Send disconnect message if your backend requires it
            // sendMessage(getServerInfoAppEndpoint(WS_SERVER_INFO_TOPICS.APP_DISCONNECT), { ip: targetIp });
        }
        setServerInfo(null);
        setIsConnectedToServer(false);
        setIsConnecting(false);
        setConnectionError(null);
        setTargetIp(null); // Clear target IP on disconnect
    }, [targetIp, isWebSocketConnected, sendMessage]); // Added dependencies

    // --- WebSocket Handlers ---
    const handleServerInfoUpdate = useCallback((data, wsError) => {
        // Only process if we are waiting for connection info for the *correct* IP
        setIsConnecting(false);
        console.log("ServerInfoFetched: ", data);
        if (wsError || data?.error) {
            const errorMsg = data?.error || wsError?.message || "Failed to connect/retrieve server info.";
            console.error("Server Connect Error (Context):", errorMsg, data);
            setConnectionError(errorMsg);
            setServerInfo(null);
            setIsConnectedToServer(false);
            // Don't clear targetIp here, let user see what failed
        } else if (data && data.online !== undefined) {
            console.log("Server Info Received (Context):", data);
            setServerInfo(data);
            setConnectionError(null);
            if (data.online) {
                setIsConnectedToServer(true);
            } else {
                setConnectionError("Target server is offline.");
                setIsConnectedToServer(false); // Server is offline
            }
        } else {
            setConnectionError("Received invalid server data structure.");
            console.warn("Server Connect Warning (Context): Invalid data structure", data);
            setServerInfo(null);
            setIsConnectedToServer(false);
        }
    }, [isConnecting, targetIp]); // Added targetIp dependency

    const handleServerErrorUpdate = useCallback((errorData, wsError) => {
        // Only process if we are waiting for connection info
        if (isConnecting) {
            setIsConnecting(false);
            const errorMsg = errorData?.message || errorData?.error || wsError?.message || "Unknown connection error.";
            console.error("General Server Connect WS Error (Context):", errorMsg);
            setConnectionError(errorMsg);
            setServerInfo(null);
            setIsConnectedToServer(false);
        }
    }, [isConnecting]);

    // --- Connect Action ---
    const connectToServer = useCallback((ip) => {
        if (!ip || !ip.trim()) {
            setConnectionError("IP Address cannot be empty."); return;
        }
        if (isConnecting) return; // Don't stack requests
        if (!isAuthenticated) { setConnectionError("Please log in first."); return; }
        if (!isWebSocketConnected) { setConnectionError("WebSocket not connected. Please wait or refresh."); return; }

        const trimmedIp = ip.trim();
        console.log(`ServerConnectionContext: Requesting connection to ${trimmedIp}`);

        // Reset state before new connection attempt
        setServerInfo(null);
        setIsConnectedToServer(false);
        setConnectionError(null);
        setIsConnecting(true);
        setTargetIp(trimmedIp); // Set the target IP

        // Send connection request
        sendMessage(getServerInfoAppEndpoint(WS_SERVER_INFO_TOPICS.APP_CONNECT), { ip: trimmedIp });

    }, [isConnecting, isAuthenticated, isWebSocketConnected, sendMessage]);

    // --- Subscription Effect ---
    useEffect(() => {
        // Ensure cleanup if WebSocket disconnects or user logs out
        if (!isWebSocketConnected || !isAuthenticated) {
            disconnectFromServer(false); // Reset state without sending message
            subscriptions.current.info?.();
            subscriptions.current.error?.();
            subscriptions.current = { info: null, error: null };
            return;
        }

        console.log("ServerConnectionContext: Subscribing to server info topics...");

        // Unsubscribe from previous before setting up new ones
        subscriptions.current.info?.();
        subscriptions.current.error?.();

        // Subscribe to user-specific info and error topics
        subscriptions.current.info = subscribe(getServerInfoUserQueueTopic(WS_SERVER_INFO_TOPICS.USER_INFO), handleServerInfoUpdate);
        subscriptions.current.error = subscribe(getServerInfoUserQueueTopic(WS_SERVER_INFO_TOPICS.USER_ERROR), handleServerErrorUpdate);

        // Cleanup function
        return () => {
            console.log("ServerConnectionContext: Unsubscribing from server info topics...");
            subscriptions.current.info?.();
            subscriptions.current.error?.();
            subscriptions.current = { info: null, error: null };
        };
    }, [isWebSocketConnected, isAuthenticated, subscribe, handleServerInfoUpdate, handleServerErrorUpdate, disconnectFromServer]); // Added disconnectFromServer

    // --- Add effect to handle logout/auth changes explicitly ---
     useEffect(() => {
         if (!isAuthenticated) {
             console.log("ServerConnectionContext: Auth lost, ensuring disconnect.");
             disconnectFromServer(false); // Reset state on logout
         }
     }, [isAuthenticated, disconnectFromServer]);


    return (
        <ServerConnectionContext.Provider value={{
            serverInfo,
            isConnectedToServer,
            isConnecting,
            connectionError,
            targetIp, // Expose the target IP
            connectToServer,
            disconnectFromServer,
        }}>
            {children}
        </ServerConnectionContext.Provider>
    );
};